// ---------------------------------------------------------------------------
// C45: Form Builder — blocks-based drag-and-drop editor
// ---------------------------------------------------------------------------
// Left: block palette. Center: form preview (live). Right: block settings.
// Save to API. Design system applied.
// ---------------------------------------------------------------------------
import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Type, Mail, Phone, Hash, ChevronDown, CheckSquare, Circle,
  AlignLeft, Calendar, Heading1, FileText, Minus,
  Plus, Trash2, GripVertical, Settings, Save, Loader2,
  Upload,
} from "lucide-react";
import { useFormDetail, saveForm, createForm, type FormField } from "@/hooks/use-forms";

// ---------------------------------------------------------------------------
// Block palette config
// ---------------------------------------------------------------------------

const BLOCK_TYPES: Array<{ type: FormField["type"]; label: string; icon: typeof Type; category: "input" | "layout" }> = [
  { type: "text", label: "Text Input", icon: Type, category: "input" },
  { type: "email", label: "Email", icon: Mail, category: "input" },
  { type: "phone", label: "Phone", icon: Phone, category: "input" },
  { type: "number", label: "Number", icon: Hash, category: "input" },
  { type: "dropdown", label: "Select", icon: ChevronDown, category: "input" },
  { type: "checkbox", label: "Checkbox", icon: CheckSquare, category: "input" },
  { type: "radio", label: "Radio", icon: Circle, category: "input" },
  { type: "textarea", label: "Text Area", icon: AlignLeft, category: "input" },
  { type: "date", label: "Date Picker", icon: Calendar, category: "input" },
  { type: "file", label: "File Upload", icon: Upload, category: "input" },
  { type: "heading", label: "Heading", icon: Heading1, category: "layout" },
  { type: "paragraph", label: "Paragraph", icon: FileText, category: "layout" },
  { type: "divider", label: "Divider", icon: Minus, category: "layout" },
];

function generateId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

// ---------------------------------------------------------------------------
// Field Preview (center panel)
// ---------------------------------------------------------------------------

function FieldPreview({ field, isSelected, onSelect }: { field: FormField; isSelected: boolean; onSelect: () => void }) {
  const isLayout = ["heading", "paragraph", "divider"].includes(field.type);

  if (field.type === "divider") {
    return (
      <div
        onClick={onSelect}
        className={`py-3 cursor-pointer rounded-lg transition-all ${isSelected ? "ring-2 ring-[var(--color-primary)]/30 bg-[var(--color-primary)]/5" : "hover:bg-[var(--color-surface)]"}`}
      >
        <hr className="border-[var(--color-border)]" />
      </div>
    );
  }

  if (field.type === "heading") {
    return (
      <div onClick={onSelect} className={`py-2 cursor-pointer rounded-lg px-3 transition-all ${isSelected ? "ring-2 ring-[var(--color-primary)]/30 bg-[var(--color-primary)]/5" : "hover:bg-[var(--color-surface)]"}`}>
        <h3 className="text-lg font-semibold text-[var(--color-foreground)]">{field.label || "Heading"}</h3>
      </div>
    );
  }

  if (field.type === "paragraph") {
    return (
      <div onClick={onSelect} className={`py-2 cursor-pointer rounded-lg px-3 transition-all ${isSelected ? "ring-2 ring-[var(--color-primary)]/30 bg-[var(--color-primary)]/5" : "hover:bg-[var(--color-surface)]"}`}>
        <p className="text-sm text-[var(--color-muted-foreground)]">{field.label || "Paragraph text..."}</p>
      </div>
    );
  }

  return (
    <div
      onClick={onSelect}
      className={`rounded-lg border p-3 cursor-pointer transition-all ${isSelected ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 ring-2 ring-[var(--color-primary)]/20" : "border-[var(--color-border)] hover:border-[var(--color-primary)]/30"}`}
    >
      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
        {field.label || "Untitled field"}
        {field.required && <span className="text-[var(--color-destructive)] ml-0.5">*</span>}
      </label>
      {field.type === "textarea" ? (
        <div className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] h-16">{field.placeholder || "Enter text..."}</div>
      ) : field.type === "file" ? (
        <div className="w-full rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3 text-sm text-[var(--color-muted-foreground)] flex items-center gap-2">
          <Upload className="h-4 w-4" />
          <span>{field.placeholder || "Choose a file..."}</span>
        </div>
      ) : field.type === "dropdown" ? (
        <div className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] flex justify-between">
          <span>{field.placeholder || "Select..."}</span>
          <ChevronDown className="h-4 w-4" />
        </div>
      ) : field.type === "checkbox" || field.type === "radio" ? (
        <div className="space-y-1">
          {(field.options ?? ["Option 1", "Option 2"]).map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
              <div className={`h-4 w-4 rounded${field.type === "radio" ? "-full" : ""} border border-[var(--color-border)]`} />
              {opt}
            </label>
          ))}
        </div>
      ) : (
        <div className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
          {field.placeholder || `Enter ${field.type}...`}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Settings (right panel)
// ---------------------------------------------------------------------------

function FieldSettings({ field, onChange, onDelete, onMoveUp, onMoveDown }: { field: FormField; onChange: (f: FormField) => void; onDelete: () => void; onMoveUp: () => void; onMoveDown: () => void }) {
  const isLayout = ["heading", "paragraph", "divider"].includes(field.type);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Field Settings</h3>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] transition-colors" title="Move up">
            <GripVertical className="h-3.5 w-3.5 rotate-180" />
          </button>
          <button onClick={onMoveDown} className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] transition-colors" title="Move down">
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <button onClick={onDelete} className="rounded-md p-1 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Label</label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onChange({ ...field, label: e.target.value })}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
        />
      </div>

      {!isLayout && (
        <>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Placeholder</label>
            <input
              type="text"
              value={field.placeholder ?? ""}
              onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required ?? false}
              onChange={(e) => onChange({ ...field, required: e.target.checked })}
              className="rounded border-[var(--color-border)]"
            />
            <span className="text-sm text-[var(--color-foreground)]">Required</span>
          </label>
        </>
      )}

      {(field.type === "dropdown" || field.type === "radio" || field.type === "checkbox") && (
        <div>
          <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Options (one per line)</label>
          <textarea
            value={(field.options ?? []).join("\n")}
            onChange={(e) => onChange({ ...field, options: e.target.value.split("\n").filter(Boolean) })}
            rows={4}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Builder
// ---------------------------------------------------------------------------

export default function FormBuilder() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const isNew = formId === "new";
  const { form, isLoading, error: detailError } = useFormDetail(isNew ? undefined : formId);

  const [formName, setFormName] = useState(form?.name ?? "Untitled Form");
  const [fields, setFields] = useState<FormField[]>(form?.fields ?? []);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const selectedField = fields.find((f) => f.id === selectedFieldId) ?? null;

  useEffect(() => {
    if (!form || isNew) return;

    setFormName(form.name);
    setFields(form.fields);
    setSelectedFieldId((current) => (
      current && form.fields.some((field) => field.id === current)
        ? current
        : form.fields[0]?.id ?? null
    ));
  }, [form, isNew]);

  const addField = useCallback((type: FormField["type"]) => {
    const newField: FormField = {
      id: generateId(),
      type,
      label: BLOCK_TYPES.find((b) => b.type === type)?.label ?? type,
      required: false,
    };
    if (type === "dropdown" || type === "radio" || type === "checkbox") {
      newField.options = ["Option 1", "Option 2", "Option 3"];
    }
    setFields((prev) => [...prev, newField]);
    setSelectedFieldId(newField.id);
  }, []);

  const updateField = useCallback((updated: FormField) => {
    setFields((prev) => prev.map((f) => f.id === updated.id ? updated : f));
  }, []);

  const deleteField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
  }, [selectedFieldId]);

  const moveField = useCallback((id: string, direction: "up" | "down") => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx < 0) return prev;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        const created = await createForm({ name: formName });
        await saveForm(created.id, { name: formName, fields });
        navigate(`/forms/builder/${created.id}`, { replace: true });
      } else if (formId) {
        await saveForm(formId, { name: formName, fields });
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save form");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" /></div>;
  }

  return (
    <div className="flex h-[calc(100vh-120px)] gap-0">
      {/* Left: Block Palette */}
      <div className="w-56 border-r border-[var(--color-border)] overflow-y-auto p-3">
        <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase mb-2">Input Fields</h3>
        <div className="space-y-1 mb-4">
          {BLOCK_TYPES.filter((b) => b.category === "input").map((block) => {
            const Icon = block.icon;
            return (
              <button
                key={block.type}
                onClick={() => addField(block.type)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] transition-colors"
              >
                <Icon className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                {block.label}
              </button>
            );
          })}
        </div>
        <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase mb-2">Layout</h3>
        <div className="space-y-1">
          {BLOCK_TYPES.filter((b) => b.category === "layout").map((block) => {
            const Icon = block.icon;
            return (
              <button
                key={block.type}
                onClick={() => addField(block.type)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] transition-colors"
              >
                <Icon className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                {block.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Center: Form Preview */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-lg mx-auto">
          <input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            className="w-full text-xl font-bold text-[var(--color-foreground)] bg-transparent border-none focus:outline-none mb-6"
            placeholder="Form name..."
          />

          {fields.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-[var(--color-border)] p-12 text-center">
              <Plus className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)]/30" />
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">Click a block from the left panel to add fields</p>
            </div>
          ) : (
            <div className="space-y-3">
              {fields.map((field) => (
                <FieldPreview
                  key={field.id}
                  field={field}
                  isSelected={selectedFieldId === field.id}
                  onSelect={() => setSelectedFieldId(field.id)}
                />
              ))}
            </div>
          )}

          {detailError || saveError ? (
            <div className="mt-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
              {saveError ?? detailError}
            </div>
          ) : null}

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-press inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: "var(--gradient-energy)" }}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Saving..." : "Save Form"}
            </button>
            <button
              onClick={() => navigate("/forms")}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Right: Field Settings */}
      <div className="w-64 border-l border-[var(--color-border)] overflow-y-auto p-4">
        {selectedField ? (
          <FieldSettings
            field={selectedField}
            onChange={updateField}
            onDelete={() => deleteField(selectedField.id)}
            onMoveUp={() => moveField(selectedField.id, "up")}
            onMoveDown={() => moveField(selectedField.id, "down")}
          />
        ) : (
          <div className="text-center py-12">
            <Settings className="mx-auto h-6 w-6 text-[var(--color-muted-foreground)]/30" />
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">Select a field to edit its settings</p>
          </div>
        )}
      </div>
    </div>
  );
}
